// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import Button from '../Button';
import IconButton from '../IconButton';

import './Section.less';

/**
 * `Section` component.
 */
class Section extends Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: props.collapsed,
            editing: false,
            title: props.title,
            vertical: props.vertical || false,
        };
    }

    getChildContext() {
        return {
            verticalSection: this.state.vertical,
        };
    }

    componentWillReceiveProps({ title, collapsed }) {
        const newState = { title };

        if (collapsed !== this.props.collapsed) {
            newState.collapsed = collapsed;
        }

        this.setState(newState);
    }

    handleResize = ({ bounds }) => {
        const { vertical } = this.props;
        if (vertical !== null) {
            this.setState({ vertical });
            return;
        }

        const { verticalSection } = this.context;
        if (verticalSection !== undefined) {
            this.setState({ vertical: verticalSection });
            return;
        }

        this.setState({ vertical: bounds.width < 450 });
    };

    handleCollapse = () => {
        this.setState(({ collapsed }) => ({
            collapsed: !collapsed,
        }));
    };

    handleEdit = () => {
        this.setState(({ editing }, { title }) => ({
            editing: !editing,
            title,
        }));
    };

    handleEditKey = event => {
        if (event.key === 'Escape') {
            this.handleEdit();
        } else if (event.key === 'Enter') {
            this.handleEditOk();
        }
    };

    handleTitleChange = event => {
        this.setState({ title: event.target.value });
    };

    handleEditOk = () => {
        this.props.onTitleChange(this.state.title);
        this.handleEdit();
    };

    renderCollapseButton({ baseClassName, collapsible }) {
        const { collapsed } = this.state;

        return collapsible && (
            <IconButton
                className={classNames(
                    `${baseClassName}__control-button`,
                    `${baseClassName}__control-button--collapse`
                )}
                icon={{ name: 'chevron-down', flipVertical: collapsed }}
                onClick={this.handleCollapse}
            />
        );
    }

    renderCloseButton({ baseClassName, onClose, buttons }) {
        return (onClose || buttons) && (
            <div className={`${baseClassName}__header-buttons`}>
                {buttons}

                {onClose && (
                    <IconButton
                        className={classNames(
                            `${baseClassName}__control-button`,
                            `${baseClassName}__control-button--close`,
                        )}
                        onClick={onClose}
                        icon="cross-mark"
                    />
                )}
            </div>
        );
    }

    renderTitleEditing({ baseClassName }) {
        const { title, editing } = this.state;

        return editing
            ? (
                <div className={`${baseClassName}__title-edit`}>
                    <input
                        type="text"
                        value={title}
                        onChange={this.handleTitleChange}
                        onKeyDown={this.handleEditKey}
                        autoFocus
                    />
                    <div className={`${baseClassName}__title-edit-actions`}>
                        <Button icon="check-mark" onClick={this.handleEditOk} />
                        <Button icon="cross-mark" onClick={this.handleEdit} />
                    </div>
                </div>
            )
            : title;
    }

    renderTitle({ baseClassName, onTitleChange }) {
        const { title, editing } = this.state;

        return title && (
            <div className={`${baseClassName}__title`}>
                {this.renderTitleEditing({ baseClassName })}

                {onTitleChange && !editing && (
                    <IconButton
                        className={classNames(
                            `${baseClassName}__control-button`,
                            `${baseClassName}__control-button--edit`
                        )}
                        onClick={this.handleEdit}
                        icon={{ name: 'pencil', className: `${baseClassName}__control-button-icon` }}
                    />
                )}
            </div>
        );
    }

    renderContent({ collapsible, children }) {
        const { collapsed } = this.state;

        if (collapsible && collapsed) {
            return null;
        }

        return children;
    }

    render() {
        const {
            baseClassName,
            className,
            intent,
            collapsible,
            title,
            collapsed,
            onTitleChange,
            onClose,
            buttons,
            vertical,
            children,
            ...props
        } = this.props;

        return (
            <Measure onResize={this.handleResize} bounds>
                {({ measureRef }) => (
                    <div
                        ref={measureRef}
                        className={classNames(
                            baseClassName,
                            {
                                [`${baseClassName}--${intent}`]: intent,
                                [`${baseClassName}--collapsible`]: collapsible,
                                [`${baseClassName}--vertical`]: this.state.vertical,
                            },
                            className,
                        )}
                        {...props}
                    >
                        {title && (
                            <div className={`${baseClassName}__header`}>
                                {this.renderCollapseButton({ baseClassName, collapsible })}
                                {this.renderTitle({ baseClassName, onTitleChange })}
                                {this.renderCloseButton({ baseClassName, onClose, buttons })}
                            </div>
                        )}
                        {this.renderContent({ collapsible, children })}
                    </div>
                )}
            </Measure>
        );
    }
}

Section.propTypes = {
    /**
     * Title of the section.
     */
    title: PropTypes.node,
    /**
     * Visual intent color to apply to component.
     */
    intent: PropTypes.oneOf([
        'danger',
        'warning',
        'success',
        'inactive',
    ]),
    /**
     * Is section collapsible.
     */
    collapsible: PropTypes.bool,
    /**
     * Is section collapsed now.
     */
    collapsed: PropTypes.bool,
    /**
     * Event handler of title change with inline editing form.
     */
    onTitleChange: PropTypes.func,
    /**
     * Event handler of closing section with X-sign at the right.
     */
    onClose: PropTypes.func,
    /**
     * Array of custom section action button at the right.
     */
    buttons: PropTypes.arrayOf(PropTypes.element),
    /**
     * Vertical or horizontal section view.
     * Section view will be automatically switched if this parameter is not specified explicitly.
     */
    vertical: PropTypes.bool,
    /**
     * Content of the `Section`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Section.defaultProps = {
    title: null,
    intent: null,
    collapsible: false,
    collapsed: false,
    onTitleChange: null,
    onClose: null,
    buttons: null,
    vertical: null,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}section`,
};

Section.childContextTypes = {
    verticalSection: PropTypes.bool,
};

Section.contextTypes = {
    verticalSection: PropTypes.bool,
};

export default Section;



// WEBPACK FOOTER //
// ./components/Section/Section.js